import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import Header from "./Header";
import { api } from "../server";

function Customers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [type, setType] = useState("ALL");
  const [active, setActive] = useState("ACTIVE");

  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const formRef = useRef(null);

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [customerType, setCustomerType] = useState("individual");
  const [companyName, setCompanyName] = useState("");
  const [gstNumber, setGstNumber] = useState("");
  const [isActive, setIsActive] = useState(true);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get("/api/admin/customers");
      setRows(data?.customers || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const resetForm = () => {
    setEditing(null);
    setName("");
    setPhone("");
    setEmail("");
    setPassword("");
    setCustomerType("individual");
    setCompanyName("");
    setGstNumber("");
    setIsActive(true);
  };
  const openCreate = () => {
    resetForm();
    setOpenForm(true);
    setTimeout(
      () =>
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
      50,
    );
  };
  const openEdit = (r) => {
    setEditing(r);
    setName(r.name || "");
    setPhone(r.phone || "");
    setEmail(r.email || "");
    setPassword("");
    setCustomerType(r.customerType || "individual");
    setCompanyName(r.companyName || "");
    setGstNumber(r.gstNumber || "");
    setIsActive(!!r.isActive);
    setOpenForm(true);
    setTimeout(
      () =>
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
      50,
    );
  };
  const closeForm = () => {
    setOpenForm(false);
    resetForm();
  };

  const saveCustomer = async (e) => {
    e.preventDefault();
    if (!name.trim()) return alert("Name is required.");
    if (!/^[6-9]\d{9}$/.test(phone))
      return alert("Enter a valid 10-digit phone number.");
    if (customerType === "corporate" && !companyName.trim())
      return alert("Company name is required for corporate.");

    const payload = {
      name: name.trim(),
      phone: phone.trim(),
      email: email.trim() || null,
      customerType,
      companyName: customerType === "corporate" ? companyName.trim() : null,
      gstNumber: customerType === "corporate" ? gstNumber.trim() || null : null,
      isActive,
    };
    if (!editing && password) payload.password = password;

    setSaving(true);
    try {
      if (editing) {
        const data = await api.put(
          `/api/admin/customers/${editing._id}`,
          payload,
        );
        const updated = data?.customer;
        setRows((prev) =>
          prev.map((x) => (x._id === editing._id ? { ...x, ...updated } : x)),
        );
      } else {
        const data = await api.post("/api/admin/customers", payload);
        const created = data?.customer;
        if (created) setRows((prev) => [created, ...prev]);
      }
      closeForm();
    } catch (err) {
      alert(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (id) => {
    const r = rows.find((x) => x._id === id);
    if (!r) return;
    try {
      const data = await api.put(`/api/admin/customers/${id}`, {
        isActive: !r.isActive,
      });
      const updated = data?.customer;
      setRows((prev) =>
        prev.map((x) => (x._id === id ? { ...x, ...updated } : x)),
      );
    } catch (err) {
      alert(err.message || "Update failed");
    }
  };

  const removeCustomer = async (id) => {
    if (!window.confirm("Deactivate this customer?")) return;
    try {
      await api.delete(`/api/admin/customers/${id}`);
      setRows((prev) =>
        prev.map((x) => (x._id === id ? { ...x, isActive: false } : x)),
      );
    } catch (err) {
      alert(err.message || "Delete failed");
    }
  };

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return rows
      .filter((r) => {
        const matchQ =
          !query ||
          String(r.name || "")
            .toLowerCase()
            .includes(query) ||
          String(r.phone || "")
            .toLowerCase()
            .includes(query) ||
          String(r.email || "")
            .toLowerCase()
            .includes(query) ||
          String(r.companyName || "")
            .toLowerCase()
            .includes(query);
        const matchType = type === "ALL" ? true : r.customerType === type;
        const matchActive =
          active === "ALL"
            ? true
            : active === "ACTIVE"
              ? !!r.isActive
              : !r.isActive;
        return matchQ && matchType && matchActive;
      })
      .sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [rows, q, type, active]);

  const stats = useMemo(() => {
    const total = rows.length;
    const act = rows.filter((x) => x.isActive).length;
    const corp = rows.filter((x) => x.customerType === "corporate").length;
    return { total, active: act, inactive: total - act, corporate: corp };
  }, [rows]);

  return (
    <>
      <Header />
      <div id="db-wrapper">
        <main id="page-content">
          <section className="container-fluid p-4 pt-3">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <div>
                <h3 className="mb-0 lh-1 ls-sm fw-normal">Customers</h3>
                <div className="text-secondary lh-1">
                  Individual & Corporate accounts • Search • Manage
                </div>
              </div>
              <div className="d-flex gap-1">
                <button
                  type="button"
                  className="btn btn-outline-secondary border-light px-4 text-dark bg-white"
                  onClick={() => {
                    setQ("");
                    setType("ALL");
                    setActive("ACTIVE");
                  }}
                >
                  <i className="bi bi-funnel me-2" />
                  Reset Filters
                </button>
                <button
                  type="button"
                  className="btn btn-primary px-4"
                  onClick={openCreate}
                >
                  <i className="bi bi-plus-circle me-2" />
                  Add Customer
                </button>
              </div>
            </div>

            <div className="row g-2 mb-2">
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm rounded-4 h-100">
                  <div className="card-body p-3 d-flex align-items-center gap-2">
                    <div
                      className="rounded-3 d-inline-flex align-items-center justify-content-center bg-primary-subtle text-primary"
                      style={{ width: 40, height: 40 }}
                    >
                      <i className="bi bi-people" />
                    </div>
                    <div>
                      <div className="text-secondary small lh-1">Total</div>
                      <div className="fs-5 fw-semibold lh-1 mt-1">
                        {stats.total}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm rounded-4 h-100">
                  <div className="card-body p-3 d-flex align-items-center gap-2">
                    <div
                      className="rounded-3 d-inline-flex align-items-center justify-content-center bg-success-subtle text-success"
                      style={{ width: 40, height: 40 }}
                    >
                      <i className="bi bi-check2-circle" />
                    </div>
                    <div>
                      <div className="text-secondary small lh-1">Active</div>
                      <div className="fs-5 fw-semibold lh-1 mt-1">
                        {stats.active}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm rounded-4 h-100">
                  <div className="card-body p-3 d-flex align-items-center gap-2">
                    <div
                      className="rounded-3 d-inline-flex align-items-center justify-content-center bg-info-subtle text-info"
                      style={{ width: 40, height: 40 }}
                    >
                      <i className="bi bi-building" />
                    </div>
                    <div>
                      <div className="text-secondary small lh-1">Corporate</div>
                      <div className="fs-5 fw-semibold lh-1 mt-1">
                        {stats.corporate}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm rounded-4 h-100">
                  <div className="card-body p-3 d-flex align-items-center gap-2">
                    <div
                      className="rounded-3 d-inline-flex align-items-center justify-content-center bg-danger-subtle text-danger"
                      style={{ width: 40, height: 40 }}
                    >
                      <i className="bi bi-pause-circle" />
                    </div>
                    <div>
                      <div className="text-secondary small lh-1">Inactive</div>
                      <div className="fs-5 fw-semibold lh-1 mt-1">
                        {stats.inactive}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm rounded-4">
              <div className="card-body p-3 p-md-4">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <div
                    className="rounded-3 d-inline-flex align-items-center justify-content-center bg-primary-subtle text-primary"
                    style={{ width: 40, height: 40 }}
                  >
                    <i className="bi bi-search" />
                  </div>
                  <div>
                    <div className="fw-semibold lh-1">Search & Filters</div>
                    <div className="text-secondary small">
                      Find by name, phone, email, or company
                    </div>
                  </div>
                </div>
                <div className="row g-2">
                  <div className="col-12 col-md-6">
                    <label className="form-label mb-1 fw-normal">Search</label>
                    <input
                      className="form-control"
                      placeholder="Name / phone / email / company"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                    />
                  </div>
                  <div className="col-6 col-md-3">
                    <label className="form-label mb-1 fw-normal">Type</label>
                    <select
                      className="form-select"
                      value={type}
                      onChange={(e) => setType(e.target.value)}
                    >
                      <option value="ALL">All Types</option>
                      <option value="individual">Individual</option>
                      <option value="corporate">Corporate</option>
                    </select>
                  </div>
                  <div className="col-6 col-md-3">
                    <label className="form-label mb-1 fw-normal">Status</label>
                    <select
                      className="form-select"
                      value={active}
                      onChange={(e) => setActive(e.target.value)}
                    >
                      <option value="ACTIVE">Active</option>
                      <option value="INACTIVE">Inactive</option>
                      <option value="ALL">All</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {openForm && (
              <div
                ref={formRef}
                className="card border-0 shadow-sm rounded-4 mt-2"
              >
                <div className="card-body p-3 p-md-4">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="d-flex align-items-center gap-2">
                      <div
                        className="rounded-3 d-inline-flex align-items-center justify-content-center bg-success-subtle text-success"
                        style={{ width: 40, height: 40 }}
                      >
                        <i
                          className={`bi ${editing ? "bi-pencil-square" : "bi-person-plus"}`}
                        />
                      </div>
                      <div>
                        <div className="fw-semibold lh-1">
                          {editing ? "Edit Customer" : "Add Customer"}
                        </div>
                        <div className="text-secondary small">
                          {editing
                            ? "Update customer details"
                            : "Create a new customer record"}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline-secondary border-light px-4 text-dark bg-white"
                      onClick={closeForm}
                    >
                      <i className="bi bi-x-lg me-1" />
                      Close
                    </button>
                  </div>

                  <form onSubmit={saveCustomer}>
                    <div className="row g-2">
                      <div className="col-12 col-md-6">
                        <label className="form-label mb-1 fw-normal">
                          Name *
                        </label>
                        <input
                          className="form-control"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Full name"
                          required
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label mb-1 fw-normal">
                          Phone *
                        </label>
                        <input
                          className="form-control"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          maxLength={10}
                          placeholder="10-digit mobile"
                          required
                          disabled={!!editing}
                        />
                        {editing && (
                          <small className="text-secondary">
                            Phone cannot be changed
                          </small>
                        )}
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label mb-1 fw-normal">
                          Email
                        </label>
                        <input
                          type="email"
                          className="form-control"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="optional@email.com"
                        />
                      </div>
                      {!editing && (
                        <div className="col-12 col-md-6">
                          <label className="form-label mb-1 fw-normal">
                            Password
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Default: 123456"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                          />
                        </div>
                      )}
                      <div className="col-12 col-md-6">
                        <label className="form-label mb-1 fw-normal">
                          Customer Type *
                        </label>
                        <select
                          className="form-select"
                          value={customerType}
                          onChange={(e) => setCustomerType(e.target.value)}
                        >
                          <option value="individual">Individual</option>
                          <option value="corporate">Corporate</option>
                        </select>
                      </div>
                      {customerType === "corporate" && (
                        <>
                          <div className="col-12 col-md-6">
                            <label className="form-label mb-1 fw-normal">
                              Company Name *
                            </label>
                            <input
                              className="form-control"
                              value={companyName}
                              onChange={(e) => setCompanyName(e.target.value)}
                              placeholder="Company name"
                            />
                          </div>
                          <div className="col-12 col-md-6">
                            <label className="form-label mb-1 fw-normal">
                              GST Number
                            </label>
                            <input
                              className="form-control"
                              value={gstNumber}
                              onChange={(e) => setGstNumber(e.target.value)}
                              placeholder="GST / TIN"
                            />
                          </div>
                        </>
                      )}
                      <div className="col-12">
                        <div className="form-check form-switch mt-2">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="custActive"
                            checked={isActive}
                            onChange={(e) => setIsActive(e.target.checked)}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="custActive"
                          >
                            Active
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="d-flex gap-2 mt-3 justify-content-end">
                      <button
                        type="button"
                        className="btn btn-light px-4"
                        onClick={closeForm}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary px-4"
                        disabled={saving}
                      >
                        {saving && (
                          <span className="spinner-border spinner-border-sm me-2" />
                        )}
                        {saving
                          ? "Saving..."
                          : editing
                            ? "Update Customer"
                            : "Save Customer"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            <div className="card border-0 shadow-sm rounded-4 mt-2">
              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" />
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="text-center py-5 text-secondary">
                    <i className="bi bi-inbox fs-1 d-block mb-2 opacity-50" />
                    No customers found
                  </div>
                ) : (
                  <div className="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                    <table className="table mb-0 text-nowrap table-centered">
                      <tbody>
                        {filtered.map((r, index) => (
                          <tr key={r._id}>
                            <td>{index + 1}.</td>
                            <td className="lh-sm py-4 text-wrap">
                              <span className="text-dark">Customer ID</span>{" "}
                              &mdash; {r._id}
                              <br />
                              <span className="text-dark">
                                Name
                              </span> &mdash; {r.name}
                              <br />
                              <span className="text-dark">
                                Type
                              </span> &mdash; {r.customerType}
                              <br />
                              <span className="text-dark">
                                Phone
                              </span> &mdash; {r.phone}
                              <br />
                              <span className="text-dark">
                                Email
                              </span> &mdash; {r.email || "—"}
                              <br />
                              <span className="text-dark">
                                Company
                              </span> &mdash; {r.companyName || "—"}
                              <br />
                              <span className="text-dark">
                                Status
                              </span> &mdash;{" "}
                              {r.isActive ? (
                                <span className="text-success">Active</span>
                              ) : (
                                <span className="text-danger">Inactive</span>
                              )}
                              {r.customerType === "corporate" && (
                                <>
                                  <br />
                                  <span className="text-dark">
                                    Corp. Status
                                  </span>{" "}
                                  &mdash;{" "}
                                  <span
                                    className={
                                      r.corporateStatus === "approved"
                                        ? "text-success"
                                        : r.corporateStatus === "pending"
                                          ? "text-warning"
                                          : "text-danger"
                                    }
                                  >
                                    {r.corporateStatus}
                                  </span>
                                </>
                              )}
                            </td>
                            <td>
                              <div className="d-flex flex-column gap-1 align-items-start">
                                <span
                                  onClick={() => openEdit(r)}
                                  className="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Update
                                </span>
                                <span
                                  onClick={() => toggleActive(r._id)}
                                  className={`badge cursor-pointer px-4 rounded-3 btn-py-sm ${
                                    r.isActive
                                      ? "bg-warning-soft"
                                      : "bg-success-soft"
                                  }`}
                                >
                                  {r.isActive ? "Deactivate" : "Activate"}
                                </span>
                                <span
                                  onClick={() => removeCustomer(r._id)}
                                  className="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Delete
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </section>
        </main>
      </div>
    </>
  );
}

export default Customers;
